import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Container,
    Paper,
    Group,
    Stack,
    Title,
    Text,
    Tabs,
    Button,
    Table,
    Badge,
    ActionIcon,
    Modal,
    TextInput,
    Select,
    NumberInput,
    Switch,
    CopyButton,
    Tooltip,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconPlus, IconPencil, IconTrash, IconCopy, IconBan } from "@tabler/icons-react";
import HeroSectionV2 from '../../../components/HeroSectionV2';
import Billede1 from '../../../Images/Billlede-1.png';
import { getAuthHeaders } from "../../../api/authHelper"; // tilpas stien

const apiUrl = process.env.REACT_APP_API_URL;

const axiosConfig = {
    headers: getAuthHeaders() as Record<string, string>,
};

type AdminEventDto = { eventId: number; name: string };
type AdminPersonDto = { personId: number; name: string; email?: string | null };

type LocationDto = {
    id: number;
    eventId: number;
    nameDK: string;
    nameEN?: string | null;
    locationType: "pickup" | "dropoff" | "both" | string;
    sortOrder: number;
    isActive: boolean;
};

type TimeWindowDto = {
    id: number;
    eventId: number;
    date: string; // yyyy-MM-dd
    startTime: string; // HH:mm
    endTime: string;   // HH:mm
    intervalMinutes: number;
    bookingMaxDaysAhead: number;
    isActive: boolean;
};

type TokenDto = {
    id: number;
    token: string;
    eventId: number;
    personId: number;
    expiresAtUtc: string;
    isActive: boolean;
    personName?: string | null;
    email?: string | null;
};

type AdminExperienceBookingDto = {
    ExperienceBookingId: number;
    eventId: number;
    personId: number;
    personName: string;
    email?: string | null;
    whatsappCountryCode?: string | null;
    whatsappNumber?: string | null;
    pickupLocationName: string;
    dropoffLocationName: string;
    pickupDate: string;
    pickupTime: string;
    passengerCount: number;
    status: string;
    createdAtUtc: string;
};

type AdminExperienceBookingTotalsDto = {
    pickupDate: string;
    totalBookings: number;
    totalPassengers: number;
};

type AdminExperienceBookingSlotDto = {
    pickupDate: string;
    pickupTime: string;
    totalBookings: number;
    totalPassengers: number;
};

export default function ExperienceAdminPage() {
    const [events, setEvents] = useState<AdminEventDto[]>([]);
    const [eventId, setEventId] = useState<number | null>(null);

    const [locations, setLocations] = useState<LocationDto[]>([]);
    const [windows, setWindows] = useState<TimeWindowDto[]>([]);
    const [tokens, setTokens] = useState<TokenDto[]>([]);

    const [loading, setLoading] = useState(false);

    // Modals
    const [locModalOpen, setLocModalOpen] = useState(false);
    const [locEditing, setLocEditing] = useState<LocationDto | null>(null);

    const [winModalOpen, setWinModalOpen] = useState(false);
    const [winEditing, setWinEditing] = useState<TimeWindowDto | null>(null);

    const [tokenModalOpen, setTokenModalOpen] = useState(false);

    // Token person search
    const [personQuery, setPersonQuery] = useState("");
    const [personResults, setPersonResults] = useState<AdminPersonDto[]>([]);
    const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
    const [expiresDays, setExpiresDays] = useState(30);

    //Booking
    const [bookingDate, setBookingDate] = useState<string | null>(null);
    const [bookings, setBookings] = useState<AdminExperienceBookingDto[]>([]);
    const [bookingTotals, setBookingTotals] = useState<AdminExperienceBookingTotalsDto[]>([]);
    const [bookingSlots, setBookingSlots] = useState<AdminExperienceBookingSlotDto[]>([]);

    useEffect(() => {
        (async () => {
            const res = await axios.get(`${apiUrl}/api/experience/admin/events`, axiosConfig);
            setEvents(res.data);
            if (res.data?.length) setEventId(res.data[0].eventId);
        })();
    }, []);

    const eventOptions = useMemo(
        () => events.map((e) => ({ value: String(e.eventId), label: e.name })),
        [events]
    );

    const refreshAll = async (eid: number) => {
        setLoading(true);
        try {
            const [loc, win, tok, totalsRes] = await Promise.all([
                axios.get(`${apiUrl}/api/experience/admin/locations`, { ...axiosConfig, params: { eventId: eid } }),
                axios.get(`${apiUrl}/api/experience/admin/timewindows`, { ...axiosConfig, params: { eventId: eid } }),
                axios.get(`${apiUrl}/api/experience/admin/tokens`, { ...axiosConfig, params: { eventId: eid, activeOnly: false } }),
                axios.get(`${apiUrl}/api/experience/admin/bookings/totals`, { ...axiosConfig, params: { eventId: eid } }),
            ]);
            setLocations(loc.data);
            setWindows(win.data);
            setTokens(tok.data);
            setBookingTotals(totalsRes.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (eventId) refreshAll(eventId);
    }, [eventId]);

    // Person search (debounce light)
    useEffect(() => {
        const q = personQuery.trim();
        if (!eventId || q.length < 2) {
            setPersonResults([]);
            return;
        }
        const t = setTimeout(async () => {
            const res = await axios.get(`${apiUrl}/api/experience/admin/persons/search`, {
                ...axiosConfig,
                params: { eventId, q },
            });
            setPersonResults(res.data);
        }, 250);

        return () => clearTimeout(t);
    }, [personQuery, eventId]);

    useEffect(() => {
        if (!eventId) return;

        (async () => {
            const res = await axios.get(`${apiUrl}/api/experience/admin/bookings`, {
                ...axiosConfig,
                params: { eventId, date: bookingDate ?? undefined },
            });
            setBookings(res.data);

            if (bookingDate) {
                const slots = await axios.get(`${apiUrl}/api/experience/admin/bookings/slots`, {
                    ...axiosConfig,
                    params: { eventId, date: bookingDate },
                });
                setBookingSlots(slots.data);
            } else {
                setBookingSlots([]);
            }
        })();
    }, [eventId, bookingDate]);

    const bookingDateOptions = useMemo(
        () => bookingTotals.map((t) => ({ value: t.pickupDate, label: `${t.pickupDate} (${t.totalPassengers} pers.)` })),
        [bookingTotals]
    );

    const cancelBooking = async (id: number) => {
        if (!eventId) return;
        await axios.post(`${apiUrl}/api/experience/admin/bookings/${id}/cancel`, axiosConfig);
        showNotification({ title: "OK", message: "Booking annulleret" });
        // refresh bookings
        const res = await axios.get(`${apiUrl}/api/experience/admin/bookings`, {
            params: { eventId, date: bookingDate ?? undefined },
        });
        setBookings(res.data);
    };

    const bookingLink = (token: string) => `${window.location.origin}/experience/book?token=${token}`;

    // --------------------
    // Location form state
    // --------------------
    const [locNameDK, setLocNameDK] = useState("");
    const [locNameEN, setLocNameEN] = useState("");
    const [locType, setLocType] = useState<"pickup" | "dropoff" | "both">("both");
    const [locSort, setLocSort] = useState<number>(0);
    const [locActive, setLocActive] = useState(true);

    const [activeTab, setActiveTab] = useState<string | null>("locations");

    const onTabChange = async (tab: string | null) => {
        setActiveTab(tab);
        if (eventId) await refreshAll(eventId);
    };

    const openCreateLocation = () => {
        setLocEditing(null);
        setLocNameDK("");
        setLocNameEN("");
        setLocType("both");
        setLocSort(0);
        setLocActive(true);
        setLocModalOpen(true);
    };

    const openEditLocation = (l: LocationDto) => {
        setLocEditing(l);
        setLocNameDK(l.nameDK);
        setLocNameEN(l.nameEN ?? "");
        setLocType((l.locationType as any) ?? "both");
        setLocSort(l.sortOrder);
        setLocActive(l.isActive);
        setLocModalOpen(true);
    };

    const saveLocation = async () => {
        if (!eventId) return;
        if (!locNameDK.trim()) {
            showNotification({ title: "Mangler", message: "NameDK er påkrævet" });
            return;
        }

        if (!locEditing) {
            await axios.post(`${apiUrl}/api/experience/admin/locations`, {
                ...axiosConfig,
                eventId,
                nameDK: locNameDK,
                nameEN: locNameEN || null,
                locationType: locType,
                sortOrder: locSort,
            });
            showNotification({ title: "Gemt", message: "Sted oprettet" });
        } else {
            await axios.put(`${apiUrl}/api/experience/admin/locations/${locEditing.id}`, {
                ...axiosConfig,
                nameDK: locNameDK,
                nameEN: locNameEN || null,
                locationType: locType,
                sortOrder: locSort,
                isActive: locActive,
            });
            showNotification({ title: "Gemt", message: "Sted opdateret" });
        }

        setLocModalOpen(false);
        await refreshAll(eventId);
    };

    const disableLocation = async (id: number) => {
        if (!eventId) return;
        await axios.delete(`${apiUrl}/api/experience/admin/locations/${id}`);
        showNotification({ title: "OK", message: "Sted deaktiveret" });
        await refreshAll(eventId);
    };

    // --------------------
    // Time window form
    // --------------------
    const [winDate, setWinDate] = useState("");
    const [winStart, setWinStart] = useState("08:00");
    const [winEnd, setWinEnd] = useState("18:00");
    const [winInterval, setWinInterval] = useState(30);
    const [winMaxAhead, setWinMaxAhead] = useState(1);
    const [winActive, setWinActive] = useState(true);

    const openCreateWindow = () => {
        setWinEditing(null);
        setWinDate("");
        setWinStart("08:00");
        setWinEnd("18:00");
        setWinInterval(30);
        setWinMaxAhead(1);
        setWinActive(true);
        setWinModalOpen(true);
    };

    const openEditWindow = (w: TimeWindowDto) => {
        setWinEditing(w);
        setWinDate(w.date);
        setWinStart(w.startTime);
        setWinEnd(w.endTime);
        setWinInterval(w.intervalMinutes);
        setWinMaxAhead(w.bookingMaxDaysAhead);
        setWinActive(w.isActive);
        setWinModalOpen(true);
    };

    const saveWindow = async () => {
        if (!eventId) return;
        if (!winDate) {
            showNotification({ title: "Mangler", message: "Dato er påkrævet (yyyy-MM-dd)" });
            return;
        }

        if (!winEditing) {
            await axios.post(`${apiUrl}/api/experience/admin/timewindows`, {
                ...axiosConfig,
                eventId,
                date: winDate,
                startTime: winStart,
                endTime: winEnd,
                intervalMinutes: winInterval,
                bookingMaxDaysAhead: winMaxAhead,
            });
            showNotification({ title: "Gemt", message: "Dag/tider oprettet" });
        } else {
            await axios.put(`${apiUrl}/api/experience/admin/timewindows/${winEditing.id}`, {
                ...axiosConfig,
                date: winDate,
                startTime: winStart,
                endTime: winEnd,
                intervalMinutes: winInterval,
                bookingMaxDaysAhead: winMaxAhead,
                isActive: winActive,
            });
            showNotification({ title: "Gemt", message: "Dag/tider opdateret" });
        }

        setWinModalOpen(false);
        await refreshAll(eventId);
    };

    const disableWindow = async (id: number) => {
        if (!eventId) return;
        await axios.delete(`${apiUrl}/api/experience/admin/timewindows/${id}`, axiosConfig);
        showNotification({ title: "OK", message: "Dag/tider deaktiveret" });
        await refreshAll(eventId);
    };

    // --------------------
    // Tokens
    // --------------------
    const openCreateToken = () => {
        setPersonQuery("");
        setPersonResults([]);
        setSelectedPersonId(null);
        setExpiresDays(30);
        setTokenModalOpen(true);
    };

    const createToken = async () => {
        if (!eventId || !selectedPersonId) {
            showNotification({ title: "Mangler", message: "Vælg person" });
            return;
        }
        await axios.post(`${apiUrl}/api/experience/admin/tokens`, {
            ...axiosConfig,
            eventId,
            personId: selectedPersonId,
            expiresInDays: expiresDays,
        });
        showNotification({ title: "OK", message: "Token oprettet/returneret" });
        setTokenModalOpen(false);
        await refreshAll(eventId);
    };

    const disableToken = async (id: number) => {
        if (!eventId) return;
        await axios.post(`${apiUrl}/api/experience/admin/tokens/${id}/disable`);
        showNotification({ title: "OK", message: "Token deaktiveret" });
        await refreshAll(eventId);
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Experience"
                titleDa="Oplevelser"
                descriptionEn="Setup for day experiences"
                descriptionDa="Opsætnings til Oplevelser"
                imageSrc={Billede1}
            />
            <Container size="xl" py="xl">
                <Paper withBorder p="lg" radius="md">
                    <Group justify="space-between" align="flex-end">
                        <Stack gap={2}>
                            <Title order={3}>Experience Admin</Title>
                            <Text c="dimmed" size="sm">
                                Opret steder, dage/tider og tokens til Experience booking
                            </Text>
                        </Stack>

                        <Select
                            label="Vælg event"
                            data={eventOptions}
                            value={eventId ? String(eventId) : null}
                            onChange={(v) => setEventId(v ? Number(v) : null)}
                            searchable
                            w={320}
                        />
                    </Group>
                </Paper>

                <Paper withBorder p="lg" radius="md" mt="md">
                    <Tabs value={activeTab} onChange={onTabChange}>
                        <Tabs.List>
                            <Tabs.Tab value="locations">Steder</Tabs.Tab>
                            <Tabs.Tab value="timewindows">Dage & tider</Tabs.Tab>
                            <Tabs.Tab value="tokens">Tokens</Tabs.Tab>
                            <Tabs.Tab value="bookings">Bookinger</Tabs.Tab>
                        </Tabs.List>

                        {/* LOCATIONS */}
                        <Tabs.Panel value="locations" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Steder</Text>
                                <Button leftSection={<IconPlus size={16} />} onClick={openCreateLocation} disabled={!eventId}>
                                    Opret sted
                                </Button>
                            </Group>

                            <Table.ScrollContainer minWidth={900}>
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Navn (DK)</Table.Th>
                                            <Table.Th>Type</Table.Th>
                                            <Table.Th>Sort</Table.Th>
                                            <Table.Th>Status</Table.Th>
                                            <Table.Th></Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {locations.map((l) => (
                                            <Table.Tr key={l.id}>
                                                <Table.Td>{l.nameDK}</Table.Td>
                                                <Table.Td>
                                                    <Badge variant="light">{l.locationType}</Badge>
                                                </Table.Td>
                                                <Table.Td>{l.sortOrder}</Table.Td>
                                                <Table.Td>
                                                    <Badge color={l.isActive ? "green" : "gray"} variant="light">
                                                        {l.isActive ? "Active" : "Inactive"}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group justify="flex-end" gap="xs">
                                                        <Tooltip label="Rediger">
                                                            <ActionIcon variant="light" onClick={() => openEditLocation(l)}>
                                                                <IconPencil size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                        <Tooltip label="Deaktiver">
                                                            <ActionIcon color="red" variant="light" onClick={() => disableLocation(l.id)}>
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        </Tabs.Panel>

                        {/* TIMEWINDOWS */}
                        <Tabs.Panel value="timewindows" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Dage & tider</Text>
                                <Button leftSection={<IconPlus size={16} />} onClick={openCreateWindow} disabled={!eventId}>
                                    Opret dag/tider
                                </Button>
                            </Group>

                            <Table.ScrollContainer minWidth={900}>
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Dato</Table.Th>
                                            <Table.Th>Fra</Table.Th>
                                            <Table.Th>Til</Table.Th>
                                            <Table.Th>Interval</Table.Th>
                                            <Table.Th>Max dage frem</Table.Th>
                                            <Table.Th>Status</Table.Th>
                                            <Table.Th></Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {windows.map((w) => (
                                            <Table.Tr key={w.id}>
                                                <Table.Td>{w.date}</Table.Td>
                                                <Table.Td>{w.startTime}</Table.Td>
                                                <Table.Td>{w.endTime}</Table.Td>
                                                <Table.Td>{w.intervalMinutes} min</Table.Td>
                                                <Table.Td>{w.bookingMaxDaysAhead}</Table.Td>
                                                <Table.Td>
                                                    <Badge color={w.isActive ? "green" : "gray"} variant="light">
                                                        {w.isActive ? "Active" : "Inactive"}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group justify="flex-end" gap="xs">
                                                        <Tooltip label="Rediger">
                                                            <ActionIcon variant="light" onClick={() => openEditWindow(w)}>
                                                                <IconPencil size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                        <Tooltip label="Deaktiver">
                                                            <ActionIcon color="red" variant="light" onClick={() => disableWindow(w.id)}>
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        </Tabs.Panel>

                        {/* TOKENS */}
                        <Tabs.Panel value="tokens" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Tokens</Text>
                                <Button leftSection={<IconPlus size={16} />} onClick={openCreateToken} disabled={!eventId}>
                                    Opret token
                                </Button>
                            </Group>

                            <Table.ScrollContainer minWidth={1000}>
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Person</Table.Th>
                                            <Table.Th>Email</Table.Th>
                                            <Table.Th>Token</Table.Th>
                                            <Table.Th>Expires</Table.Th>
                                            <Table.Th>Status</Table.Th>
                                            <Table.Th></Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {tokens.map((t) => {
                                            const short = t.token?.slice(0, 8) + "…";
                                            const url = bookingLink(t.token);
                                            return (
                                                <Table.Tr key={t.id}>
                                                    <Table.Td>{t.personName ?? t.personId}</Table.Td>
                                                    <Table.Td>{t.email ?? "-"}</Table.Td>
                                                    <Table.Td>{short}</Table.Td>
                                                    <Table.Td>{new Date(t.expiresAtUtc).toLocaleString()}</Table.Td>
                                                    <Table.Td>
                                                        <Badge color={t.isActive ? "green" : "gray"} variant="light">
                                                            {t.isActive ? "Active" : "Inactive"}
                                                        </Badge>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Group justify="flex-end" gap="xs">
                                                            <CopyButton value={url}>
                                                                {({ copied, copy }) => (
                                                                    <Tooltip label={copied ? "Kopieret!" : "Kopiér link"}>
                                                                        <ActionIcon variant="light" onClick={copy}>
                                                                            <IconCopy size={16} />
                                                                        </ActionIcon>
                                                                    </Tooltip>
                                                                )}
                                                            </CopyButton>

                                                            <Tooltip label="Deaktiver token">
                                                                <ActionIcon color="red" variant="light" onClick={() => disableToken(t.id)}>
                                                                    <IconBan size={16} />
                                                                </ActionIcon>
                                                            </Tooltip>
                                                        </Group>
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        </Tabs.Panel>
                        <Tabs.Panel value="bookings" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Bookinger</Text>
                                <Badge variant="light">
                                    {bookings.reduce((sum, b) => sum + (b.status === "confirmed" ? b.passengerCount : 0), 0)} pers.
                                </Badge>
                            </Group>

                            <Paper withBorder p="md" radius="md" mb="md">
                                <Group align="flex-end" justify="space-between" wrap="wrap">
                                    <Select
                                        label="Dato"
                                        placeholder="Alle datoer"
                                        data={bookingDateOptions}
                                        value={bookingDate}
                                        onChange={(v) => setBookingDate(v)}
                                        searchable
                                        w={320}
                                    />
                                    <Button variant="default" onClick={() => setBookingDate(null)}>
                                        Vis alle
                                    </Button>
                                </Group>
                            </Paper>

                            {bookingDate && bookingSlots.length > 0 && (
                                <Paper withBorder p="md" radius="md" mb="md">
                                    <Text fw={700} mb="sm">Timeslots ({bookingDate})</Text>
                                    <Table.ScrollContainer minWidth={600}>
                                        <Table striped highlightOnHover>
                                            <Table.Thead>
                                                <Table.Tr>
                                                    <Table.Th>Tid</Table.Th>
                                                    <Table.Th>Bookinger</Table.Th>
                                                    <Table.Th>Personer</Table.Th>
                                                </Table.Tr>
                                            </Table.Thead>
                                            <Table.Tbody>
                                                {bookingSlots.map((s) => (
                                                    <Table.Tr key={s.pickupTime}>
                                                        <Table.Td>{s.pickupTime}</Table.Td>
                                                        <Table.Td>{s.totalBookings}</Table.Td>
                                                        <Table.Td>{s.totalPassengers}</Table.Td>
                                                    </Table.Tr>
                                                ))}
                                            </Table.Tbody>
                                        </Table>
                                    </Table.ScrollContainer>
                                </Paper>
                            )}

                            <Paper withBorder p="md" radius="md">
                                <Text fw={700} mb="sm">Alle bookinger</Text>

                                <Table.ScrollContainer minWidth={1100}>
                                    <Table striped highlightOnHover>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Person</Table.Th>
                                                <Table.Th>Email</Table.Th>
                                                <Table.Th>Whatsapp</Table.Th>
                                                <Table.Th>Afhentning</Table.Th>
                                                <Table.Th>Destination</Table.Th>
                                                <Table.Th>Dato</Table.Th>
                                                <Table.Th>Tid</Table.Th>
                                                <Table.Th>Pers.</Table.Th>
                                                <Table.Th>Status</Table.Th>
                                                <Table.Th></Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {bookings.map((b) => (
                                                <Table.Tr key={b.ExperienceBookingId}>
                                                    <Table.Td>{b.personName}</Table.Td>
                                                    <Table.Td>{b.email ?? "-"}</Table.Td>
                                                    <Table.Td>{b.whatsappCountryCode}-{b.whatsappNumber}</Table.Td>
                                                    <Table.Td>{b.pickupLocationName}</Table.Td>
                                                    <Table.Td>{b.dropoffLocationName}</Table.Td>
                                                    <Table.Td>{b.pickupDate}</Table.Td>
                                                    <Table.Td>{b.pickupTime}</Table.Td>
                                                    <Table.Td>{b.passengerCount}</Table.Td>
                                                    <Table.Td>
                                                        <Badge color={b.status === "confirmed" ? "green" : "gray"} variant="light">
                                                            {b.status}
                                                        </Badge>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Group justify="flex-end" gap="xs">
                                                            {b.status === "confirmed" && (
                                                                <Button size="xs" color="red" variant="light" onClick={() => cancelBooking(b.ExperienceBookingId)}>
                                                                    Annullér
                                                                </Button>
                                                            )}
                                                        </Group>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </Table.ScrollContainer>
                            </Paper>
                        </Tabs.Panel>

                    </Tabs>
                </Paper>

                {/* LOCATION MODAL */}
                <Modal opened={locModalOpen} onClose={() => setLocModalOpen(false)} title={locEditing ? "Rediger sted" : "Opret sted"} centered>
                    <Stack>
                        <TextInput label="Navn (DK)" value={locNameDK} onChange={(e) => setLocNameDK(e.currentTarget.value)} />
                        <TextInput label="Navn (EN)" value={locNameEN} onChange={(e) => setLocNameEN(e.currentTarget.value)} />

                        <Select
                            label="Type"
                            data={[
                                { value: "pickup", label: "pickup" },
                                { value: "dropoff", label: "dropoff" },
                                { value: "both", label: "both" },
                            ]}
                            value={locType}
                            onChange={(v: any) => setLocType(v)}
                        />

                        <NumberInput label="SortOrder" value={locSort} onChange={(v) => setLocSort(Number(v) || 0)} />

                        {locEditing && (
                            <Switch label="Aktiv" checked={locActive} onChange={(e) => setLocActive(e.currentTarget.checked)} />
                        )}

                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setLocModalOpen(false)}>Annullér</Button>
                            <Button onClick={saveLocation}>Gem</Button>
                        </Group>
                    </Stack>
                </Modal>

                {/* TIMEWINDOW MODAL */}
                <Modal opened={winModalOpen} onClose={() => setWinModalOpen(false)} title={winEditing ? "Rediger dag/tider" : "Opret dag/tider"} centered>
                    <Stack>
                        <TextInput label="Dato (yyyy-MM-dd)" value={winDate} onChange={(e) => setWinDate(e.currentTarget.value)} />
                        <Group grow>
                            <TextInput label="Start (HH:mm)" value={winStart} onChange={(e) => setWinStart(e.currentTarget.value)} />
                            <TextInput label="Slut (HH:mm)" value={winEnd} onChange={(e) => setWinEnd(e.currentTarget.value)} />
                        </Group>

                        <Group grow>
                            <NumberInput label="Interval (min)" value={winInterval} onChange={(v) => setWinInterval(Number(v) || 30)} min={5} />
                            <NumberInput label="Max dage frem" value={winMaxAhead} onChange={(v) => setWinMaxAhead(Number(v) || 1)} min={0} />
                        </Group>

                        {winEditing && (
                            <Switch label="Aktiv" checked={winActive} onChange={(e) => setWinActive(e.currentTarget.checked)} />
                        )}

                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setWinModalOpen(false)}>Annullér</Button>
                            <Button onClick={saveWindow}>Gem</Button>
                        </Group>
                    </Stack>
                </Modal>

                {/* TOKEN MODAL */}
                <Modal opened={tokenModalOpen} onClose={() => setTokenModalOpen(false)} title="Opret token" centered>
                    <Stack>
                        <TextInput
                            label="Søg person (fornavn/efternavn)"
                            value={personQuery}
                            onChange={(e) => setPersonQuery(e.currentTarget.value)}
                            placeholder="Skriv min. 2 tegn..."
                        />

                        <Select
                            label="Vælg person"
                            data={personResults.map((p) => ({ value: String(p.personId), label: `${p.name} (${p.email ?? "-"})` }))}
                            value={selectedPersonId ? String(selectedPersonId) : null}
                            onChange={(v) => setSelectedPersonId(v ? Number(v) : null)}
                            searchable
                            nothingFoundMessage="Ingen resultater"
                        />

                        <NumberInput label="Udløber om (dage)" value={expiresDays} onChange={(v) => setExpiresDays(Number(v) || 30)} min={1} max={365} />

                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setTokenModalOpen(false)}>Annullér</Button>
                            <Button onClick={createToken}>Opret</Button>
                        </Group>
                    </Stack>
                </Modal>
            </Container>
        </div>
    );
}